import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './MercadoPagoRatesTable.scss';

const MercadoPagoRatesTable = ({ cuotasMP, productPrice }) => {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const bankOptions = useMemo(() => {
        if (!cuotasMP || !Array.isArray(cuotasMP)) return [];
        const options = [];
        let globalIndex = 0;

        cuotasMP.forEach((brand) => {
            if (brand.issuers && Array.isArray(brand.issuers)) {
                brand.issuers.forEach((issuer) => {
                    const rates = issuer.rates || [];
                    const hasPromo = rates.some(r => {
                        const rateVal = parseFloat(r.rate ?? r.installment_rate ?? 0);
                        return rateVal === 0 && r.installments >= 2;
                    });
                    options.push({
                        label: `${brand.brand.toUpperCase()} - ${issuer.bank_name}`,
                        rates: rates,
                        key: `${brand.brand}-${issuer.bank_name}-${globalIndex}`,
                        originalIndex: globalIndex,
                        hasPromo: hasPromo
                    });
                    globalIndex++;
                });
            }
        });
        return options;
    }, [cuotasMP]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const currentOption = selectedIndex !== null ? bankOptions[selectedIndex] : null;
    const currentRates = currentOption ? currentOption.rates : [];
    const numericPrice = parseFloat(productPrice) || 0;

    const filteredOptions = bankOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (originalIndex, label) => {
        setSelectedIndex(originalIndex);
        setSearchTerm(label);
        setIsOpen(false);
    };

    const handleInputClick = () => {
        setIsOpen(true);
    };

    const handleClearSearch = (e) => {
        e.stopPropagation();
        setSearchTerm('');
        setSelectedIndex(null);
        setIsOpen(true);
    };

    useEffect(() => {
        if (currentOption && !isOpen && searchTerm === '') {
            setSearchTerm(currentOption.label);
        }
    }, [currentOption, isOpen, searchTerm]);

    if (bankOptions.length === 0) return null;

    return (
        <div className="mp-rates-table">
            <h4>Medios de pago con Mercado Pago</h4>
            
            <div className="mp-select-container" ref={dropdownRef}>
                <label>Buscá tu tarjeta o banco</label>
                
                <div className="mp-custom-dropdown">
                    <input 
                        type="text"
                        className="mp-search-input"
                        placeholder="Escribí para buscar... (Ej: Visa Galicia)"
                        value={searchTerm}
                        onClick={handleInputClick}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsOpen(true);
                        }}
                    />
                    {searchTerm.length > 0 ? (
                        <span 
                            className="mp-clear-icon" 
                            onClick={handleClearSearch}
                            title="Borrar búsqueda"
                        >
                            ✕
                        </span>
                    ) : (
                        <span className="mp-arrow">▼</span>
                    )}

                    {isOpen && (
                        <ul className="mp-options-list">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((opt) => (
                                    <li 
                                        key={opt.key} 
                                        className={selectedIndex !== null && opt.originalIndex === selectedIndex ? 'selected' : ''}
                                        onClick={() => handleSelect(opt.originalIndex, opt.label)}
                                    >
                                        <span>{opt.label}</span>
                                        {opt.hasPromo && (
                                            <span className="mp-promo-badge" title="Cuotas sin interés">
                                                💳 Sin interés
                                            </span>
                                        )}
                                    </li>
                                ))
                            ) : (
                                <li className="no-results">No se encontraron resultados</li>
                            )}
                        </ul>
                    )}
                </div>
            </div>

            <div className="mp-rates-list">
                {currentOption ? (
                    <>
                        <div className="mp-table-header">
                            <span>Cuotas</span>
                            <span>Intereses</span>
                            <span>Total a pagar</span>
                        </div>
                        {currentRates.map((rateData, i) => {
                            const rawRate = rateData.rate ?? rateData.installment_rate ?? 0;
                            const rate = parseFloat(rawRate);
                            const installments = rateData.installments;

                            if (!installments) return null;

                            const isInterestFree = Math.abs(rate) < 0.01;
                            let totalAmount, interestAmount;

                            if (isInterestFree) {
                                totalAmount = numericPrice;
                                interestAmount = 0;
                            } else {
                                totalAmount = numericPrice * (1 + (rate / 100));
                                interestAmount = totalAmount - numericPrice;
                            }

                            const installmentAmount = totalAmount / installments;

                            return (
                                <div key={i} className={`mp-row ${isInterestFree ? 'interest-free' : ''}`}>
                                    <div className="col-detail">
                                        <span className="installment-text">
                                            {installments}x ${installmentAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                        {isInterestFree && <span className="promo-text">Mismo precio</span>}
                                    </div>
                                    <span className={`col-interest ${isInterestFree ? 'green' : ''}`}>
                                        {isInterestFree ? 'Sin interés' : `$${interestAmount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
                                    </span>
                                    <span className="col-total">
                                        ${totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            );
                        })}
                    </>
                ) : (
                    <div className="mp-empty-state">
                        Seleccioná tu tarjeta y banco para ver el detalle de cuotas.
                    </div>
                )}
            </div>
        </div>
    );
};

MercadoPagoRatesTable.propTypes = {
    cuotasMP: PropTypes.array,
    productPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

MercadoPagoRatesTable.defaultProps = {
    cuotasMP: [],
};

export default MercadoPagoRatesTable;